<template>
    <div>
        <h1>Congrats, you have finished!</h1>
        <h2 v-show="attemptNum > 0">
            This is attempt # {{attemptNum}}
        </h2>
        <hr>
        <div v-if="!reviewMode">
            <h3>Please take a moment to review this case:</h3>
            <div v-if="!peerReviewAccept && !peerReviewReject">
                <br><br>
                <center style="display: flex; justify-content: space-around; max-width: 800px; margin: auto;">
                    <div style="display: inline-block">
                        <b>Quality Rating:</b><br><br>
                        <star-rating 
                        v-model="qualityRating" 
                        :star-size="30" 
                        @rating-selected="submitReview"
                        style="display: inline-block">
                        </star-rating>
                        <div class="rating-anchor">
                            <span v-if="qualityRating == 1">
                                Flawed case with many mistakes or little value.
                            </span>
                            <span v-if="qualityRating == 2">
                                Case has issues, but has valuable learning points.
                            </span>
                            <span v-if="qualityRating == 3">
                                Case is average, but could be improved.
                            </span>
                            <span v-if="qualityRating == 4">
                                Case is valuable for learning.
                            </span>
                            <span v-if="qualityRating == 5">
                                Case is outstanding, everyone should try it.
                            </span>
                        </div>
                    </div>
                    <div style="display: inline-block">
                        <b>Difficulty Rating:</b><br><br>
                        <star-rating 
                        v-model="difficultyRating" 
                        :star-size="30" 
                        :max-rating="3" 
                        @rating-selected="submitReview"
                        style="display: inline-block">
                        </star-rating>
                        <div class="rating-anchor">
                            <span v-if="difficultyRating == 1">
                                Case is not challenging enough for me.
                            </span>
                            <span v-if="difficultyRating == 2">
                                Case might be out of my comfort zone, but is doable.
                            </span>
                            <span v-if="difficultyRating == 3">
                                Case is too difficult for me.
                            </span>
                        </div>
                    </div>
                </center>
            </div>
            <div v-if="qualityRating && qualityRating <= 2" style="margin-top: 40px; padding: 20px;">
                <b>Quality Issues:</b>
                <br>
                <md-checkbox v-model="issues.factErr" @change="submitReview">
                    Case Contains Factual Errors
                </md-checkbox> 
                <md-checkbox v-model="issues.citations" @change="submitReview">
                    Case Is Missing Citations
                </md-checkbox> 
                <md-checkbox v-model="issues.badExp" @change="submitReview">
                    Answers Are Poorly Explained
                </md-checkbox> 
            </div>
            <div v-if="$store.state.jwtClaims.isEducator && qualityRating && difficultyRating && !peerReviewAccept && !peerReviewReject" 
            style="max-width: 600px; margin: auto; margin-top: 30px; border: 1px dotted black; padding: 15px;">
                <b>
                    You are registered as a Caseen educator.
                    <br>
                    If you'd like to peer review this case, click one of the options below:
                </b>
                <div style="max-width: 400px; margin: auto; margin-top: 15px; display: flex; justify-content: space-around;">
                    <md-button class="md-raised md-primary" v-if="qualityRating != undefined && qualityRating > 2" @click="submitPeerReview(true)">
                       Approve Case 
                    </md-button>
                    <md-button class="md-raised md-accent" @click="submitPeerReview(false)">
                       Reject Case 
                    </md-button>
                </div>
            </div>
            <br><br>
            <b>Comments:</b>
            <br><br>
            <quill-editor ref="myTextEditor" 
                @blur="submitReview()"
                v-model="comments"
                :options="{'placeholder': 'Tell the author what you thought about the case. How could it be improved? What did you think worked well?'}"
                style="max-width: 800px; margin: auto;"
            ></quill-editor>
            <br><br>
            <md-button 
            style="background-color: #fcc56c;"
            class="md-raised"
            @click="skipReviewing()"
            v-show="!(qualityRating != undefined && difficultyRating != undefined)">
                <b style="font-size: 75%">Opt out of reviewing this case</b>
            </md-button>
        </div>
        <div v-show="reviewMode || (qualityRating != undefined && difficultyRating != undefined)">
            <h3>Showing data from {{caseAttemptsRecorded}} attempts of this case.</h3>
            <div class="result-container">
                <ResponseDashboard :slideDeck="slideDeck" :responseData="responseData" :indicatorData="performanceLog"/>
            </div>
            <hr> 
            <div>
                <md-button class="md-raised md-primary" @click="finishCase">
                    Return to Case List
                </md-button>
                <RewardScreen :pointsEarned="pointsEarned"/>
            </div>
        </div>
        <div v-show="(qualityRating == undefined || difficultyRating == undefined) && !reviewMode">
            <br>
            <h3>
                <i>Results will appear here once you have completed your review.</i>
            </h3>
            <br>
            <hr>
        </div>
    </div>
</template>

<script>

import StarRating from 'vue-star-rating'
import Editable from "../APIRequests.js"
import APIRequests from "../APIRequests.js"
import ResponseDashboard from "../classrooms/ResponseDashboard.vue"
import RewardScreen from "../RewardScreen.vue"

export default {
    props: {
        caseID: {
            type: String
        },
        slideDeck: {
            type: Object
        },
        performanceLog: {
            type: Array
        },
        reviewMode: {
            type: Boolean,
            default: false
        }
    },
    data: function(){
        return {
            responseData: [],
            caseAttemptsRecorded: 0,
            pointsEarned: 0,
            qualityRating: undefined,
            difficultyRating: undefined,
            issues: {
                factErr: false,
                citations: false,
                badExp: false,
            },
            comments: "",
            peerReviewAccept: false,
            peerReviewReject: false,
        }
    },
    components: {
        StarRating,
        Editable,
        ResponseDashboard,
        RewardScreen,
    },
    methods: {
        finishCase: function(){
            this.$emit('toggleNavBlock')
            if(this.reviewMode) {
                // Redirect to case list
                let redirectURL = "/cases"
                if(this.$route.query.classroom && this.$route.query.assignment){
                    let classroomID = this.$route.query.classroom
                    let assignmentID = this.$route.query.assignment
                    redirectURL = "/classroom/" + classroomID + "?viewer=true&assignment=" + assignmentID
                }
                window.location.href = redirectURL; // Changed to refresh of page to update learner score in sidebar
            } else {
                // Change the points earned which causes the reward dialog to pop up
                // after the point reward has been displayed user will be redirected to case list via RewardScreen.vue 
                this.pointsEarned = 200 + ((this.qualityRating == 0 && this.difficultyRating == 0) ? 0 : 50);
            }
        },
        skipReviewing: function(){
            // Submits whatever review the user has entered
            this.submitReview()
            // Sets quality and difficulty to 0 allowing the review dashboard to be shown
            if(!this.qualityRating) this.qualityRating = 0
            if(!this.difficultyRating) this.difficultyRating = 0
        },
        submitReview: function() {
            let reviewData = {
                //id: ObjectID(),
                cid: this.caseID,
                attempt: this.attemptNum,
                quality: this.qualityRating ? this.qualityRating : 0,
                difficulty: this.difficultyRating ? this.difficultyRating : 0,
                issues: this.qualityRating <= 2 ? this.issues : {},
                comments: this.comments,
                peerAccept: this.peerReviewAccept,
                peerReject: this.peerReviewReject,
            }
            reviewData.reporting = {}
            if(this.$route.query.classroom){
                reviewData.reporting.classroomID = this.$route.query.classroom
            }
            if(this.$route.query.assignment){
                reviewData.reporting.assignmentID = this.$route.query.assignment
            }
            APIRequests.postJSONData(
                this.$store.state.api_url + "/api/cases/" + this.caseID + "/updateReview",
                reviewData,
                this.$store.state.jwtToken
            ).then(() => {
                this.$emit("notify", "Saved review!")
            }).catch(([xhrStatus, xhrResp]) => {
                this.$emit("notify", "Error, failed to save edits: " + xhrStatus + " - " + xhrResp)
            }) 
        },
        getResponses: function(){
            var api_url = this.$store.state.api_url + "/api/editor/" + this.caseID + "/responses"
            return APIRequests.getJSONData(
                api_url, 
                this.$store.state.jwtToken
            ).then((data) => {
                if(!data) data=[]
                let caseAttempts = {}
                data.forEach((responseEntry) => {
                    caseAttempts[responseEntry.pid + "-" + responseEntry.attempt] = true
                })
                this.responseData = data
                this.caseAttemptsRecorded = Object.keys(caseAttempts).length
            })
        },
        submitPeerReview: function(peerReview){
            let confirmReview = confirm("By submitting a peer review, you affirm that you are qualified to evaluate this case's contents.\nClick OK below in order to " + (peerReview ? "accept" : "reject") + " this case.")
            if(!confirmReview){
                return
            }
            if(peerReview){
                this.peerReviewAccept = true
                this.peerReviewReject = false
            } else {
                this.peerReviewReject = true
                this.peerReviewAccept = false
            }
            this.submitReview()
        },
    },
    computed: {
        attemptNum: function(){
            if(this.performanceLog[0]){
                return this.performanceLog[0].attempt
            }
            return 0
        }
    },
    mounted: function(){
        this.$nextTick(() => {
            this.getResponses()
        })
    }
}
</script>

<style scoped>
    h2{
        margin-top: 0px;
    }
    div >>> .vue-star-rating-rating-text{
        display: none;
    }
    .rating-anchor{
        width: 180px;
        font-style: italic; 
        margin-top: 12px;
    }
    .result-container{
        width: 100%;
        display: flex;
        justify-content: space-around;
        flex-wrap: wrap;
    }
    .md-card{
        display: inline-block;
        margin: 23px 10px;
    }
</style>